/*
 * useZuteilungGui.tsx - Zuteilung und Auftrag beschleunigen.
 *
 * Created by Dr. Maximillian Dornseif 2023-11-10 in huWaWi3 33.7.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { useSchema } from '@hudora/hd-react-components'
import { findAllByKey } from '@hudora/hd-ts-util'
import { dateTimeifyTyped } from 'any-to-luxon'
import gql from 'graphql-tag'
import { JSONSchema7 } from 'json-schema'
import { DateTime } from 'luxon'
import * as React from 'react'
import { useQuery } from 'urql'

import { AUFTRAG_FRAGMENT, LAGER_FRAGMENT } from '../../fragments'
import { ICommandBarItemProps } from '../../lib/muipanels'
import { useEditDialog } from '../components/useEditDialog'
import { useToastMutation } from '../components/useToastMutation'
import { AUFTRAG_TEXT_MUTATION } from './WaWiAuftragEinerQuery'

const AUFTRAG_BOOLEAN_MUTATION = gql`
  mutation AuftragBooleanMutation(
    $key: ID!
    $oldRev: String
    $fieldName: String
    $value: Boolean
    $message: String
  ) {
    result: updateHwAuftragBoolean(
      auftragKeyString: $key
      auftragRev: $oldRev
      fieldName: $fieldName
      value: $value
      message: $message
    ) {
      auftrag {
        ...auftragFragment
      }
      success
      message
      oldRev
      newRev
      duration
    }
  }
  ${AUFTRAG_FRAGMENT}
`

export const AUFTRAG_DATE_MUTATION = gql`
  mutation AuftragsDateMutation(
    $key: ID!
    $oldRev: String
    $fieldName: String
    $value: Date
    $message: String
  ) {
    result: updateHwAuftragDate(
      auftragKeyString: $key
      auftragRev: $oldRev
      fieldName: $fieldName
      value: $value
      message: $message
    ) {
      auftrag {
        ...auftragFragment
      }
      success
      message
      oldRev
      newRev
      duration
    }
  }
  ${AUFTRAG_FRAGMENT}
`

const STUECKELN_MUTATION = gql`
  mutation StueckelnMutation($key: ID!, $message: String) {
    updateHwAuftragStueckeln303(key: $key, message: $message) {
      auftrag {
        ...auftragFragment
      }
      success
      message
    }
  }
  ${AUFTRAG_FRAGMENT}
`

const LAGER_LIST_QUERY = gql`
  query LagerAktivePages {
    nodes: hwLagerPages(filter: { ist_aktiv: { eq: true } }, sort: { fields: [ist_aktiv], order: ASC }) {
      edges {
        cursor
        node {
          ...lagerFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${LAGER_FRAGMENT}
`

export function useZuteilungGui(auftrag) {
  const [disabledTeillieferung] = React.useState(false)
  const { schema } = useSchema('HwAuftrag')
  const [, mutateTeillieferung] = useToastMutation({
    mutation: AUFTRAG_BOOLEAN_MUTATION,
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev, fieldName: 'teillieferung_gewuenscht' },
    fieldTitle: (schema?.properties?.teillieferung_gewuenscht as any)?.title,
  })
  const [, mutateAnlieferdatumVon] = useToastMutation({
    mutation: AUFTRAG_DATE_MUTATION,
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev, fieldName: 'anlieferdatum_von' },
    fieldTitle: (schema?.properties?.anlieferdatum_von as any)?.title,
  })
  const [, mutateAnlieferdatumBis] = useToastMutation({
    mutation: AUFTRAG_DATE_MUTATION,
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev, fieldName: 'anlieferdatum_bis' },
    fieldTitle: (schema?.properties?.anlieferdatum_bis as any)?.title,
  })
  const [, mutateStueckeln] = useToastMutation({
    mutation: STUECKELN_MUTATION,
    variables: { key: auftrag._keyStr },
    fieldTitle: (schema?.properties?.anlieferdatum_bis as any)?.title,
  })
  const [editLager, lagerDialog] = useEditDialog({
    subSchema: schema?.properties?.lager as JSONSchema7,
    propertyName: 'lager',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_TEXT_MUTATION,
  })

  // Wir patchen das Schema mit der Liste der aktiven Läger
  // um die im Dialog darzustellen.
  const [{ data: lagerData }] = useQuery({ query: LAGER_LIST_QUERY })
  const laeger = findAllByKey(lagerData, 'node').sort((a, b) => a.designator.localeCompare(b.designator))
  if (schema?.properties?.lager) {
    const lagerProp = schema.properties.lager as Record<string, any>
    lagerProp.enum = laeger.map((l) => l.designator)
    lagerProp.enumNames = laeger.map((l) => `${l.designator} - ${l.name}`)
    if (auftrag.lager && !lagerProp.enum.includes(auftrag.lager)) {
      lagerProp.enum.push(auftrag.lager)
      lagerProp.enumNames.push(`${auftrag.lager}: legacy Lager aus Auftrag`)
    }
  }

  const auftragNichtBearbeitbar = auftrag.status < 100 || auftrag.status > '600'
  const auftragNichtBearbeitbarZugeteilt = auftrag.status < 100 || auftrag.status > '400'
  const anlieferdatumVon = dateTimeifyTyped(auftrag.anlieferdatum_von)
  const today = DateTime.now()
  const anlieferdatumBis = dateTimeifyTyped(auftrag.anlieferdatum_bis)
  const zeitfenster = anlieferdatumBis.diff(anlieferdatumVon).as('days')

  const item: ICommandBarItemProps = {
    key: 'zuteilung',
    text: 'Zuteilung',
    ariaLabel: 'Zuteilung',
    iconOnly: false,
    iconProps: { iconName: 'DocumentSet' },
    disabled: !(auftrag.status >= '100ready'),
    subMenuProps: {
      items: [
        {
          key: 'Kreditlimitprüfungvorziehen',
          text: 'Kreditlimitprüfung Vorziehen',
          iconProps: { iconName: 'Bank' },
          disabled: auftrag.status !== '299geprueft',
          // onClick: () => belegErzeugen('Auftragsbestätigung'),
        },
        {
          key: 'teillieferung',
          text: auftrag.teillieferung_gewuenscht ? 'Teillieferung verbieten' : 'Teillieferung wünschen',
          iconProps: { iconName: auftrag.teillieferung_gewuenscht ? 'GridViewLarge' : 'ViewDashboard' },
          disabled: disabledTeillieferung || auftrag.kundenlieferschein_vorhanden,
          onClick: () =>
            mutateTeillieferung({
              value: !auftrag.teillieferung_gewuenscht,
              message: `teillieferung_gewuenscht ${
                !auftrag.teillieferung_gewuenscht ? 'gesetzt' : 'aufgehoben'
              }`,
            }),
        },
        {
          key: 'AnlieferdatumVorziehen',
          text: 'Anlieferdatum jetzt!',
          iconProps: { iconName: 'GotoToday' },
          disabled: auftragNichtBearbeitbarZugeteilt || anlieferdatumVon < today,
          onClick: () =>
            mutateAnlieferdatumVon({
              value: today.plus({ days: 1 }).toISODate(),
              message: 'Anlieferdatum vorziehen',
            }),
        },
        {
          key: 'LieferfensterVerkleinern',
          text: `Lieferfenster minimieren`,
          iconProps: { iconName: 'DateTime' },
          disabled: auftragNichtBearbeitbarZugeteilt || zeitfenster < 6,
          onClick: () =>
            mutateAnlieferdatumBis({
              value: anlieferdatumVon.plus({ days: 5 }).toISODate(),
              fieldName: 'anlieferdatum_bis',
              message: 'Lieferfenster verkleinert',
            }),
        },
        {
          key: 'Sofortzuteilung',
          text: 'Sofortzuteilung anfordern',
          iconProps: { iconName: 'FastForward' },
          disabled: auftragNichtBearbeitbarZugeteilt || true,
          // onClick: () => belegErzeugen('Zollrechnung'),
        },
        //
        {
          key: 'stueckeln',
          text: 'Lieferung beliebig Stückeln',
          iconProps: { iconName: 'Quantity' },
          disabled: (!auftragNichtBearbeitbarZugeteilt) || auftrag.status > '303',
          onClick: () =>
            mutateStueckeln({
              message: 'Radikal-Zuteilung angefordert',
            }),
        },
        {
          key: 'Lager',
          text: 'Lager setzen',
          iconProps: { iconName: 'HomeVerify' },
          disabled: auftragNichtBearbeitbar,
          onClick: () => editLager(auftrag.lager),
        },
      ],
    },
  }
  return [item, lagerDialog]
}
